import React, { useCallback, useEffect, useMemo } from 'react';
import type { ExtendedComponentSchema } from 'formiojs';
import { Components } from 'formiojs';
import type {
  EhsFormioReactProps,
  FormioBuilderInfo,
  FormioBuilderSchema
} from '../';
import { EhsFormioReactComponent } from '../';
import { DatePicker, DateTimePicker } from '../../../form';
import type { Component } from '@formio/react';
import { useQuestionnaireValidation } from '../../questionnaire/useQuestionnaireValidation';

//START-EHS-REACT-FORMIO-DEFINITION---------------------------------------------
export type FormioDateTimePickerConstructor = {
  maxDateToday: boolean;
  timepicker: boolean;
};
export type FormioDateTimePickerSettings = {
  maxDate: boolean;
  timepicker: boolean;
};
export const FormioDateTimePickerType = 'ehsdatetimequestionnairepicker';

export class EhsFormioDateTimePicker extends EhsFormioReactComponent<
  FormioDateTimePickerSettings,
  FormioDateTimePickerConstructor
> {
  /**
   * defines the default schema of the custom component
   * https://help.form.io/developers/custom-components#component-methods
   */
  static schema(
    extend?: FormioBuilderSchema
  ): ExtendedComponentSchema<FormioBuilderSchema> {
    return Components.components.field.schema({
      type: FormioDateTimePickerType,
      label: 'common.date',
      key: FormioDateTimePickerType,
      maxDateToday: false,
      timepicker: true,
      ...extend
    });
  }

  /**
   * defines how the component will look in the Form Editor (Builder)
   * https://help.form.io/developers/custom-components#component-methods
   */
  static get builderInfo(): FormioBuilderInfo {
    return {
      title: 'Ehs-DateTimepicker',
      group: 'advanced',
      icon: 'calendar',
      documentation: '/developers/custom-components',
      weight: 40,
      schema: EhsFormioDateTimePicker.schema()
    };
  }

  /**
   * defines the fields available in the Editor/Builder
   * https://help.form.io/developers/form-builder#overriding-behavior
   */
  static editForm() {
    return {
      components: [
        {
          type: 'textfield',
          key: 'label',
          label: 'label',
          tooltip: 'Please add only valid translation keys'
        },
        {
          type: 'checkbox',
          input: true,
          key: 'maxDateToday',
          label: 'MaxDate today',
          tooltip: 'Set the highest possible date to now.',
          weight: 20
        },
        {
          type: 'checkbox',
          input: true,
          key: 'timepicker',
          label: 'Add Time',
          tooltip: 'Show (Date- &) Timepicker .',
          weight: 30
        }
      ]
    };
  }

  //Begin Non static definitions-------------------------------------
  private ehsMaxDate = false;
  private ehsTimepicker = true;

  extractFields(component: Component<FormioDateTimePickerConstructor>): void {
    this.ehsMaxDate = component.maxDateToday;
    this.ehsTimepicker = component.timepicker;
  }

  attachEhsReact(): [
    (props: EhsFormioReactProps<FormioDateTimePickerSettings>) => JSX.Element,
    FormioDateTimePickerSettings
  ] {
    return [
      Datepicker,
      { maxDate: this.ehsMaxDate, timepicker: this.ehsTimepicker }
    ];
  }
}

//END-EHS-REACT-FORMIO-DEFINITION-----------------------------------------------
function Datepicker({
  value,
  onChange,
  label,
  settings,
  required,
  readOnly,
  defaultValue,
  keyForComponent
}: EhsFormioReactProps<FormioDateTimePickerSettings>): JSX.Element {
  const { errorMessage, isQuestionnaire } = useQuestionnaireValidation(
    keyForComponent
  );
  const onChangeWrapper = useCallback(
    (selectedDates: Date | number | string | null): void => {
      if (!selectedDates) {
        onChange('');
        return;
      }

      if (
        typeof selectedDates === 'number' ||
        typeof selectedDates === 'string'
      ) {
        onChange(transformDateToISOString(selectedDates, !settings.timepicker));
        return;
      }

      onChange(transformDateToISOString(selectedDates, !settings.timepicker));
    },
    [onChange, settings.timepicker]
  );
  const wrapperValue = useMemo(() => {
    return (value ?? '') == '' ? null : new Date(value);
  }, [value]);

  useEffect(() => {
    if (defaultValue) {
      onChangeWrapper(defaultValue);
    }
  }, [defaultValue, onChangeWrapper]);
  const onClear = useCallback(() => onChangeWrapper(null), [onChangeWrapper]);
  return useMemo(() => {
    if (settings.timepicker) {
      return (
        <DateTimePicker
          onChange={onChangeWrapper}
          value={wrapperValue}
          label={label}
          maxDate={settings.maxDate ? new Date() : ''}
          required={required}
          readOnly={readOnly}
          error={errorMessage}
          labelOnTop={isQuestionnaire}
          onClear={onClear}
        />
      );
    } else {
      return (
        <DatePicker
          variant="single"
          onChange={onChangeWrapper}
          value={wrapperValue}
          label={label}
          maxDate={settings.maxDate ? new Date() : ''}
          required={required}
          readOnly={readOnly}
          error={errorMessage}
          labelOnTop={isQuestionnaire}
          onClear={onClear}
        />
      );
    }
  }, [
    settings.timepicker,
    settings.maxDate,
    onChangeWrapper,
    wrapperValue,
    label,
    required,
    readOnly,
    errorMessage,
    isQuestionnaire,
    onClear
  ]);
}

function transformDateToISOString(
  date: Date | number | string,
  isDatePicked: boolean
): string {
  const dateToTransform = new Date(date);

  if (!isDatePicked) {
    return dateToTransform.toISOString() ?? '';
  }

  return new Date(
    Date.UTC(
      dateToTransform.getFullYear(),
      dateToTransform.getMonth(),
      dateToTransform.getDate()
    )
  )
    .toISOString()
    .split('T')[0];
}
